import { DollarSign, Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-6 md:py-12 pb-20 md:pb-12">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl md:rounded-2xl p-5 md:p-8 mb-6 md:mb-8 text-white">
          <div className="flex items-center mb-3 md:mb-4">
            <RefreshCw className="h-8 w-8 md:h-12 md:w-12 mr-3 md:mr-4" />
            <div>
              <h1 className="text-2xl md:text-4xl font-bold">Refund & Cancellation Policy</h1>
              <p className="text-primary-100 text-xs md:text-base mt-1 md:mt-2">Last updated: October 16, 2025</p>
            </div>
          </div>
          <p className="text-sm md:text-lg text-primary-50">
            Understanding our refund and cancellation policies for RAPSAPP services.
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-8 space-y-6 md:space-y-8">
          
          {/* Section 1 */}
          <section>
            <div className="flex items-start mb-4">
              <Clock className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Cancellation Policy</h2>
                <div className="text-gray-700 space-y-3">
                  <p>We understand that plans can change. Our cancellation policy is designed to be fair to both customers and service professionals.</p>
                  
                  <div className="mt-4 space-y-4">
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                        <div>
                          <p className="font-semibold text-green-900">24+ Hours Before Service</p>
                          <p className="text-green-800 mt-1">Free cancellation with <strong>100% refund</strong></p>
                          <p className="text-sm text-green-700 mt-1">No questions asked - full refund processed within 5-7 business days</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                        <div>
                          <p className="font-semibold text-amber-900">12-24 Hours Before Service</p>
                          <p className="text-amber-800 mt-1">Cancellation with <strong>50% refund</strong></p>
                          <p className="text-sm text-amber-700 mt-1">Service professional preparation costs are deducted</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                      <div className="flex items-start">
                        <XCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                        <div>
                          <p className="font-semibold text-red-900">Less Than 12 Hours Before Service</p>
                          <p className="text-red-800 mt-1"><strong>No refund</strong> available</p>
                          <p className="text-sm text-red-700 mt-1">Professional has already committed and prepared for service</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <CheckCircle className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Emergency Cancellations</h2>
                <div className="text-gray-700 space-y-3">
                  <p>We understand that genuine emergencies can occur. The following situations may qualify for special consideration:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Medical emergencies</strong> - Hospitalization or serious illness (medical certificate required)</li>
                    <li><strong>Natural disasters</strong> - Floods, earthquakes, or severe weather conditions</li>
                    <li><strong>Death in family</strong> - Immediate family member (documentation required)</li>
                    <li><strong>Government restrictions</strong> - Lockdowns or travel bans</li>
                  </ul>
                  
                  <div className="mt-4 bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-900"><strong>Note:</strong> Emergency cancellations must be reported within 24 hours with supporting documentation. Our team will review and process eligible refunds on a case-by-case basis.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <XCircle className="h-6 w-6 text-red-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Service Professional Cancellation</h2>
                <div className="text-gray-700 space-y-3">
                  <p>If a service professional cancels your booking:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Immediate notification</strong> - You will be notified as soon as possible</li>
                    <li><strong>100% full refund</strong> - No questions asked</li>
                    <li><strong>Alternative professional</strong> - We will offer a replacement at the same price</li>
                    <li><strong>Priority rebooking</strong> - Get priority for your next booking</li>
                    <li><strong>Compensation</strong> - ₹200 credit for future bookings as an apology</li>
                  </ul>
                  
                  <p className="mt-4">Our goal is to ensure minimal disruption to your plans.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <DollarSign className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Refund Process</h2>
                <div className="text-gray-700 space-y-3">
                  <p><strong>Refund Timeline:</strong></p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Initiation:</strong> Refund initiated within 24 hours of cancellation</li>
                    <li><strong>Processing:</strong> 5-7 business days to reflect in your account</li>
                    <li><strong>Bank delays:</strong> Additional 2-3 days depending on your bank</li>
                  </ul>
                  
                  <p className="mt-4"><strong>Refund Method:</strong></p>
                  <p>Refunds are processed to the original payment method used during booking:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Credit/Debit Card:</strong> 5-7 business days</li>
                    <li><strong>Net Banking:</strong> 5-7 business days</li>
                    <li><strong>UPI:</strong> 3-5 business days</li>
                    <li><strong>Wallet:</strong> 1-3 business days</li>
                  </ul>
                  
                  <div className="mt-4 bg-gray-100 p-4 rounded-lg">
                    <p className="text-gray-900"><strong>Tracking Your Refund:</strong></p>
                    <p className="text-gray-700 mt-2">You can track your refund status in the "My Bookings" section of your account. You will also receive email updates at each stage.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Service Quality Issues</h2>
            <div className="text-gray-700 space-y-3">
              <p>If you're unsatisfied with the service quality:</p>
              
              <div className="space-y-3 mt-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">Step 1: Report Immediately</p>
                  <p className="text-gray-700 mt-1">Contact us within 24 hours of service completion with details and photos (if applicable)</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">Step 2: Investigation</p>
                  <p className="text-gray-700 mt-1">Our team will review your complaint and may contact you for additional information</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">Step 3: Resolution</p>
                  <p className="text-gray-700 mt-1">Based on findings, we may offer:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-gray-700">
                    <li>Free service rework</li>
                    <li>Partial refund (25-75% based on severity)</li>
                    <li>Full refund (for major quality issues)</li>
                    <li>Credit for future bookings</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-yellow-900"><strong>Important:</strong> Complaints must be raised within 24 hours of service completion. Claims made after this period may not be eligible for refunds.</p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Non-Refundable Situations</h2>
            <div className="text-gray-700 space-y-3">
              <p>Refunds will not be provided in the following cases:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Customer not available</strong> - If you're not present at the scheduled time</li>
                <li><strong>Incorrect address</strong> - Wrong or incomplete address provided</li>
                <li><strong>Service completed</strong> - After service has been successfully completed</li>
                <li><strong>Customer change of mind</strong> - After service has started</li>
                <li><strong>Unreasonable expectations</strong> - Expectations beyond service scope</li>
                <li><strong>Third-party services</strong> - Issues with third-party products/materials</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Rescheduling Services</h2>
            <div className="text-gray-700 space-y-3">
              <p>Want to reschedule instead of canceling?</p>
              
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg mt-3">
                <p className="text-green-900"><strong>Free Rescheduling:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-green-800">
                  <li>Up to 12 hours before service - completely free</li>
                  <li>Subject to professional availability</li>
                  <li>Reschedule up to 2 times per booking</li>
                  <li>Easy process through "My Bookings" section</li>
                </ul>
              </div>
              
              <p className="mt-4">Rescheduling is a better option than canceling if you still need the service!</p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">8. How to Request Cancellation/Refund</h2>
            <div className="text-gray-700 space-y-3">
              <p><strong>Option 1: Through Your Account</strong></p>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>Login to your RAPSAPP account</li>
                <li>Go to "My Bookings"</li>
                <li>Select the booking you want to cancel</li>
                <li>Click "Cancel Booking"</li>
                <li>Select reason and confirm</li>
              </ol>
              
              <p className="mt-4"><strong>Option 2: Contact Customer Support</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Phone:</strong> +91 7018401540 (Mon-Sun, 8 AM - 8 PM)</li>
                <li><strong>Email:</strong> rapsappservice@gmail.com</li>
                <li><strong>WhatsApp:</strong> +91 7018401540</li>
              </ul>
              
              <p className="mt-4">Please provide your booking ID for faster processing.</p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Policy Updates</h2>
            <div className="text-gray-700 space-y-3">
              <p>RAPSAPP reserves the right to modify this refund policy at any time. Changes will be effective immediately upon posting on our website. The policy applicable at the time of booking will govern that specific booking.</p>
            </div>
          </section>

          {/* Contact */}
          <section className="border-t pt-8 bg-gray-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Need Help?</h2>
            <div className="text-gray-700 space-y-2">
              <p>If you have questions about our refund policy or need assistance with a cancellation:</p>
              <div className="space-y-1 mt-4">
                <p><strong>Email:</strong> <a href="mailto:rapsappservice@gmail.com" className="text-primary-600 hover:underline">rapsappservice@gmail.com</a></p>
                <p><strong>Phone:</strong> <a href="tel:+917018401540" className="text-primary-600 hover:underline">+91 7018401540</a></p>
                <p><strong>WhatsApp:</strong> <a href="https://wa.me/917018401540" className="text-primary-600 hover:underline">+91 7018401540</a></p>
                <p><strong>Address:</strong> Himachal Pradesh, India</p>
              </div>
              <p className="mt-4 text-sm text-gray-600">Our customer support team is available Monday to Sunday, 8 AM - 8 PM IST</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
